import { IUser } from "@stringf/services/types";
import { atomWithStorage } from "jotai/utils";
import { atom } from "jotai";

export interface AuthState {
  ready: boolean;
  user?: IUser;
  anoUser?: IUser;
}

/**
 * `getOnInit`: localStorage is synchronous, so the stored session is there on the
 * first read. Without it the atom reported its default until something
 * subscribed, and the `/y` guard had to show a spinner until `ready` flipped,
 * which a signed-out visitor's never does.
 */
export const authStateAtom = atomWithStorage<AuthState>(
  "auth",
  { ready: false },
  undefined,
  { getOnInit: true }
);

export const setUserAtom = atom(null, (get, set, newUser: IUser) => {
  set(authStateAtom, { ...get(authStateAtom), user: newUser, ready: true });
});

export const setAnoUserAtom = atom(null, (get, set, newAnoUser: IUser) => {
  set(authStateAtom, {
    ...get(authStateAtom),
    anoUser: newAnoUser,
    ready: true,
  });
});

/**
 * The account whose data this browser holds, read from the stored auth state rather than
 * from an atom: every tab shares localStorage, so a sign-out anywhere is seen at once
 * by anything that asks (the local message store does, on every call).
 */
export function signedInAccountId(): string | undefined {
  try {
    const stored = typeof localStorage === "undefined" ? null : localStorage.getItem("auth");
    if (!stored) return undefined;
    const { user, anoUser } = JSON.parse(stored) as Partial<AuthState>;
    return user?.id || anoUser?.id || undefined;
  } catch {
    return undefined;
  }
}
